import { PlannerWithDetails, TodoWithDetails } from '../types/databaseTypes';
import { BLOCKS_PER_HOUR, MINUTES_PER_BLOCK } from './timeBlockUtils';
import i18next from '@/translation/i18n';

function indexToTime(index: number): string {
    const hours = Math.floor((index * MINUTES_PER_BLOCK) / 60);
    const minutes = (index * MINUTES_PER_BLOCK) % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * 시간 값(숫자)을 'HH:MM' 형식의 문자열로 변환합니다.
 * @param time 0~24 사이의 시간 값 (예: 9.5는 9시 30분)
 * @returns 형식화된 시간 문자열 (예: '09:30')
 */
export const formatTime = (blockIndex: number): string => {
    const hours = Math.floor(blockIndex / BLOCKS_PER_HOUR);
    const minutes = (blockIndex % BLOCKS_PER_HOUR) * MINUTES_PER_BLOCK;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export function convertBlocksToTimeRanges(blocks: number[]): string[] {
    if (!blocks.length) return [];

    // 정렬 (원본 배열 직접 수정)
    blocks.sort((a, b) => a - b);

    const result: string[] = [];
    let start = blocks[0];
    let prev = start;

    // 단일 순회로 처리
    for (let i = 1; i <= blocks.length; i++) {
        const current = blocks[i];
        if (current !== prev + 1) {
            // 연속되지 않은 지점에서 시간 범위 문자열 생성
            result.push(`${indexToTime(start)} ~ ${indexToTime(prev + 1)}`);
            start = current;
        }
        prev = current;
    }

    return result;
}

/**
 * 시간 블록 배열에서 시작-종료 시간과 총 소요 시간을 포맷팅하는 함수
 * @param blocks 시간 블록 배열
 * @returns '시작시간 ~ 종료시간 (총 소요시간)' 형식의 문자열
 */
export const formatTimeRange = (blocks: number[]): string => {
    if (!blocks || blocks.length === 0) return '';

    // 가장 작은 블록(시작)과 가장 큰 블록(종료) 찾기
    const sortedBlocks = [...blocks].sort((a, b) => a - b);
    const minBlock = sortedBlocks[0];
    const maxBlock = sortedBlocks[sortedBlocks.length - 1];

    // 시작 및 종료 시간 포맷팅
    const startTime = formatTime(minBlock);
    const endTime = formatTime(maxBlock + 1); // 종료 블록은 +1 (해당 블록이 끝나는 시간)

    // 총 소요 시간 계산 (분 단위)
    const durationText = getDurationText(blocks);

    return `${startTime} ~ ${endTime} (${durationText})`;
};

/**
 * 시간 블록 배열의 시간을 포맷팅하는 함수
 * @param blocks 시간 블록 배열
 * @returns 'h시간 m분' 형식의 문자열
 */
export const getDurationText = (blocks: number[]) => {
    // 총 소요 시간 계산 (분 단위)
    const totalMinutes = blocks.length * MINUTES_PER_BLOCK;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // 시간 포맷팅
    let durationText = '';
    if (hours > 0) {
        durationText += `${hours}${i18next.t('common:time.hour')}`;
        if (minutes > 0) {
            durationText += ` ${minutes}${i18next.t('common:time.minute')}`;
        }
    } else {
        durationText += `${minutes}${i18next.t('common:time.minute')}`;
    }

    return durationText;
};

// 계획한 날짜 렌더링 시 사용
export function formatPlannerDateRange(planners: TodoWithDetails['planners']) {
    if (!planners || planners.length === 0) return null;

    const dates = planners.map(p => new Date(p.planner_date));
    if (dates.length === 1) {
        return dates[0].toLocaleDateString(i18next.language, {
            month: 'short',
            day: 'numeric',
        });
    }

    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

    return `${minDate.toLocaleDateString(i18next.language, {
        month: 'short',
        day: 'numeric',
    })} ~ ${maxDate.toLocaleDateString(i18next.language, {
        month: 'short',
        day: 'numeric',
    })}`;
}

export function getCurrentTodo(
    planners: PlannerWithDetails[],
    targetDate: Date
) {
    const currentHour = targetDate.getHours();
    const currentMinute = targetDate.getMinutes();
    const totalMinutes = currentHour * 60 + currentMinute;
    const currentBlock = Math.floor(totalMinutes / MINUTES_PER_BLOCK);

    return planners.find(planner => planner.blocks?.includes(currentBlock));
}
